import { useCallback } from 'react';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuthStore } from '@/stores/auth.store';
import { usePlatformContext } from '@/features/platform/use-context';
import { markTourDone } from './onboarding';

interface TourStepDef {
  selector: string;
  title: string;
  description: string;
  /** Si se indica, el paso solo aparece cuando el módulo está activo. */
  module?: string;
}

// Pasos anclados a items del sidebar (data-tour="<id>"). El paso de Inventario
// solo se incluye si el rubro activó ese módulo.
const STEPS: TourStepDef[] = [
  { selector: '[data-tour="dashboard"]', title: '¡Bienvenido a Cajita! 👋', description: 'Este es tu panel: ventas del día, caja y alertas de un vistazo.' },
  { selector: '[data-tour="pos"]', title: 'Punto de venta', description: 'Registra ventas y emite boletas/facturas en segundos.' },
  { selector: '[data-tour="documents"]', title: 'Comprobantes', description: 'Tus boletas y facturas electrónicas, listas para SUNAT.' },
  { selector: '[data-tour="products"]', title: 'Productos', description: 'Administra tu catálogo y precios.' },
  { selector: '[data-tour="inventory"]', title: 'Inventario', description: 'Controla stock, lotes y costos. Activado para tu rubro.', module: 'inventory' },
  { selector: '[data-tour="settings"]', title: 'Configuración', description: 'Datos de tu empresa, series, usuarios y tu suscripción. Aquí puedes volver a ver este recorrido.' },
];

function buildSteps(modules: string[] | undefined): DriveStep[] {
  const active = new Set(modules ?? []);
  return STEPS.filter((s) => {
    if (s.module && !active.has(s.module)) return false;
    return typeof document !== 'undefined' && document.querySelector(s.selector) !== null;
  }).map((s) => ({
    element: s.selector,
    popover: { title: s.title, description: s.description },
  }));
}

/**
 * Recorrido guiado del sistema (driver.js). `start()` arranca el tour con los
 * pasos correspondientes a los módulos activos del tenant; al terminar/saltar
 * marca el flag para no repetirlo (re-lanzable manualmente ignorando el flag).
 */
export function useProductTour() {
  const ctx = usePlatformContext();
  const tenantId = useAuthStore((s) => s.activeTenantId);
  const modules = ctx.data?.modules;

  const start = useCallback(() => {
    const steps = buildSteps(modules);
    if (steps.length === 0) return;
    const d = driver({
      showProgress: true,
      nextBtnText: 'Siguiente',
      prevBtnText: 'Atrás',
      doneBtnText: 'Listo',
      progressText: '{{current}} de {{total}}',
      steps,
      onDestroyed: () => markTourDone(tenantId),
    });
    d.drive();
  }, [modules, tenantId]);

  return { start, ready: !!modules };
}
